import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import HttpService from "../services/HttpService";
import RenderOnRole from "../components/RenderOnRole";

const emptyForm = { idDomain: "", idElement: "" };

function getErrorMessage(error) {
  return error?.response?.data?.message || error?.response?.data || error?.message || "The operation could not be completed";
}

export default function LookupTableConfig() {
  const queryClient = useQueryClient();
  const [selectedDomain, setSelectedDomain] = useState("");
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [newDomain, setNewDomain] = useState(false);
  const [feedback, setFeedback] = useState({ open: false, severity: "success", message: "" });

  const { data, isLoading, isFetching, isError, refetch } = useQuery(
    ["lookupTables"],
    () => HttpService.getAxiosClient().get(window.API_URL + "/lookup-tables"),
    { refetchOnWindowFocus: false }
  );

  const records = data?.data || [];
  const domains = useMemo(
    () => [...new Set(records.map((record) => record.idDomain))].sort(),
    [records]
  );
  const filteredRecords = selectedDomain
    ? records.filter((record) => record.idDomain === selectedDomain)
    : records;

  const closeDialog = () => {
    setDialog(null);
    setForm(emptyForm);
    setNewDomain(false);
  };

  const mutation = useMutation(
    ({ method, url, body }) =>
      HttpService.getAxiosClient()[method](window.API_URL + url, body),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["lookupTables"]);
        if (variables.method === "post") {
          setSelectedDomain(variables.body.idDomain);
        }
        closeDialog();
        setFeedback({ open: true, severity: "success", message: "Lookup table saved" });
      },
      onError: (error) =>
        setFeedback({ open: true, severity: "error", message: getErrorMessage(error) }),
    }
  );

  const deleteMutation = useMutation(
    ({ idDomain, idElement }) =>
      HttpService.getAxiosClient().delete(
        window.API_URL + `/lookup-tables/${encodeURIComponent(idDomain)}/${encodeURIComponent(idElement)}`
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["lookupTables"]);
        setFeedback({ open: true, severity: "success", message: "Lookup element deleted" });
      },
      onError: (error) =>
        setFeedback({ open: true, severity: "error", message: getErrorMessage(error) }),
    }
  );

  const submit = () => {
    const body = {
      idDomain: form.idDomain.trim(),
      idElement: form.idElement.trim(),
    };
    if (!body.idDomain || !body.idElement) {
      setFeedback({ open: true, severity: "error", message: "Domain and element are required" });
      return;
    }

    if (dialog.mode === "edit") {
      mutation.mutate({
        method: "put",
        url: `/lookup-tables/${encodeURIComponent(dialog.record.idDomain)}/${encodeURIComponent(dialog.record.idElement)}`,
        body,
      });
    } else {
      mutation.mutate({ method: "post", url: "/lookup-tables", body });
    }
  };

  const openAdd = (domain = selectedDomain, createDomain = false) => {
    setForm({ idDomain: domain, idElement: "" });
    setNewDomain(createDomain);
    setDialog({ mode: "add" });
  };

  const columns = [
    { field: "idDomain", headerName: "Domain", flex: 1, minWidth: 180 },
    { field: "idElement", headerName: "Element", flex: 2, minWidth: 260 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton onClick={() => { setForm({ ...row }); setDialog({ mode: "edit", record: row }); }} size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              onClick={() => {
                if (window.confirm(`Delete element "${row.idElement}"?`)) deleteMutation.mutate(row);
              }}
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (isLoading) return <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>;

  return (
    <RenderOnRole roles={["pc-program-super-admin"]}>
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Lookup</Typography>
          <Box display="flex" gap={1}>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={() => openAdd("", true)}>Add domain</Button>
            <Button startIcon={<AddIcon />} variant="contained" onClick={() => openAdd()}>Add element</Button>
            <IconButton onClick={refetch} title="Refresh"><RefreshIcon /></IconButton>
          </Box>
        </Box>
        <FormControl size="small" sx={{ minWidth: 280, mb: 2 }}>
          <InputLabel id="lookup-domain-label">Domain</InputLabel>
          <Select
            labelId="lookup-domain-label"
            value={selectedDomain}
            label="Domain"
            onChange={(event) => setSelectedDomain(event.target.value)}
          >
            <MenuItem value="">All domains</MenuItem>
            {domains.map((domain) => <MenuItem key={domain} value={domain}>{domain}</MenuItem>)}
          </Select>
        </FormControl>
        {isError && <Alert severity="error">Unable to load lookup table</Alert>}
        <DataGrid
          autoHeight
          rows={filteredRecords}
          columns={columns}
          getRowId={(row) => `${row.idDomain}:${row.idElement}`}
          loading={isFetching || deleteMutation.isLoading}
          components={{ Toolbar: GridToolbar }}
          pageSize={25}
          rowsPerPageOptions={[25, 50, 100]}
          disableSelectionOnClick
        />
        <Dialog open={!!dialog} onClose={closeDialog} fullWidth maxWidth="sm">
          <DialogTitle>{dialog?.mode === "edit" ? "Edit lookup element" : "Add lookup element"}</DialogTitle>
          <DialogContent>
            {dialog?.mode === "add" && !newDomain ? (
              <FormControl fullWidth margin="normal">
                <InputLabel id="lookup-add-domain-label">Domain</InputLabel>
                <Select
                  labelId="lookup-add-domain-label"
                  value={form.idDomain}
                  label="Domain"
                  onChange={(event) => {
                    if (event.target.value === "__new__") {
                      setNewDomain(true);
                      setForm({ ...form, idDomain: "" });
                    } else {
                      setForm({ ...form, idDomain: event.target.value });
                    }
                  }}
                >
                  <MenuItem value="" disabled>Select a domain</MenuItem>
                  {domains.map((domain) => <MenuItem key={domain} value={domain}>{domain}</MenuItem>)}
                  <MenuItem value="__new__">Add new domain...</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <TextField
                autoFocus
                fullWidth
                margin="normal"
                label="Domain"
                value={form.idDomain}
                onChange={(event) => setForm({ ...form, idDomain: event.target.value })}
                inputProps={{ maxLength: 50 }}
              />
            )}
            <TextField
              fullWidth
              margin="normal"
              label="Element"
              value={form.idElement}
              onChange={(event) => setForm({ ...form, idElement: event.target.value })}
              inputProps={{ maxLength: 200 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button onClick={submit} variant="contained" disabled={mutation.isLoading}>Save</Button>
          </DialogActions>
        </Dialog>
        <Snackbar open={feedback.open} autoHideDuration={4000} onClose={() => setFeedback({ ...feedback, open: false })}>
          <Alert severity={feedback.severity} onClose={() => setFeedback({ ...feedback, open: false })}>{feedback.message}</Alert>
        </Snackbar>
      </Box>
    </RenderOnRole>
  );
}